import { VerificationNames } from "shared/types";
import { CreditReport } from "types/landlord/reports";
import { ReportCompletedBanner } from "views/applications-id-report/components/ReportCompletedBanner";
import { Section } from "views/applications-id-report/components/Section";
import { Stat } from "views/applications-id-report/components/Stat";
import { InfoCircle } from "untitledui-js-base";
import { formatToCurrency } from "utils/currency";
import { ApplicantSettings } from "shared/types";

type AccountsSummaryProps = {
  creditScore?: number | null;
  pastDueAccount?: number | null;
  balanceOfCollections?: number;
  balanceOfUnpaidCollections?: number;
  bankruptcy?: number;
  bankruptcyYears?: number;
  bankruptcyCriteriaValue?: string;
  criteria?: CreditReport["criteria"];
  verificationId: number;
  completed_at?: string | null;
  isNoHit: boolean;
  applicantName: string;
  pastDueAccountType: string;
  debtBalance?: number;
  screeningCriteria?: ApplicantSettings;
};

export const CreditReportSummary = ({
  creditScore,
  pastDueAccount,
  balanceOfUnpaidCollections,
  balanceOfCollections,
  bankruptcy,
  bankruptcyYears,
  bankruptcyCriteriaValue,
  criteria,
  verificationId,
  completed_at,
  isNoHit,
  applicantName,
  debtBalance,
  pastDueAccountType,
  screeningCriteria
}: AccountsSummaryProps) => {
  return (
    <Section sectionTitle="Credit report summary" maxWidth="100%">
      {completed_at ? (
        <ReportCompletedBanner
          verificationName={VerificationNames.CreditReport}
          completedAt={completed_at}
          reportId={String(verificationId)}
        />
      ) : null}

      {isNoHit ? (
        <div className="py-6 border-b border-alpha-40 -mx-6 mb-6 w-auto">
          <div className="flex w-full justify-between items-center mx-6 mb-6">
            <InfoCircle size="24" className="text-content-100 h-6 w-6 mr-4" />
            <p className="text-lg text-content-1000 font-bold mr-auto w-auto">
              {applicantName} has no credit history
            </p>
          </div>
          <div className=""></div>
        </div>
      ) : null}

      <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
        <Stat
          title="Credit score"
          value={isNoHit ? "-" : String(creditScore ?? "--")}
          width="240px"
          hasCriteria={!isNoHit && !!criteria}
          criteriaValue={criteria?.credit_score}
          criteriaSuccess={(creditScore || 0) >= (criteria?.credit_score || 0)}
          criteriaImportance={screeningCriteria?.credit_score?.importance}
        />
        <Stat
          title="Past due accounts"
          value={
            isNoHit
              ? "-"
              : pastDueAccountType === "Percentage"
                ? pastDueAccount !== null && pastDueAccount !== undefined
                  ? `${String(pastDueAccount).slice(0, 5)}%`
                  : "--"
                : "--"
          }
          width="240px"
          hasCriteria={!isNoHit && !!criteria}
          criteriaValue={
            criteria?.past_due_account +
            (pastDueAccountType === "Percentage" ? "%" : "")
          }
          criteriaSuccess={
            (pastDueAccount || 0) <= (criteria?.past_due_account || 0)
          }
          criteriaImportance={screeningCriteria?.past_due_account?.importance}
        />
        <div className="block md:hidden col-span-2 border-b border-alpha-40 my-8" />
        <Stat
          title="Balance past due"
          value={
            isNoHit ? "-" : "$" + String(balanceOfUnpaidCollections ?? "--")
          }
          width="240px"
          hasCriteria={!isNoHit && !!criteria}
          criteriaValue={criteria?.unpaid_collections_balance}
          formattedCriteriaValue={formatToCurrency({
            cents: (criteria?.unpaid_collections_balance || 0) * 100,
            currency: "USD"
          })}
          criteriaSuccess={
            (balanceOfUnpaidCollections || 0) <=
            (criteria?.unpaid_collections_balance || 0)
          }
          criteriaImportance={
            screeningCriteria?.unpaid_collections_balance?.importance
          }
        />
        <div className="hidden md:block md:col-span-3 border-b border-alpha-40 my-8" />
        <Stat
          title="Balance of collections"
          value={isNoHit ? "-" : "$" + String(balanceOfCollections ?? "--")}
          width="240px"
          hasCriteria={!isNoHit && !!criteria}
          criteriaValue={criteria?.collections_balance}
          formattedCriteriaValue={formatToCurrency({
            cents: (criteria?.collections_balance || 0) * 100,
            currency: "USD"
          })}
          criteriaSuccess={
            (balanceOfCollections || 0) <= (criteria?.collections_balance || 0)
          }
          criteriaImportance={
            screeningCriteria?.collections_balance?.importance
          }
        />
        <div className="block md:hidden col-span-2 border-b border-alpha-40 my-8" />
        <Stat
          title="Bankruptcy"
          value={isNoHit ? "-" : String(bankruptcy ?? "--")}
          width="240px"
          hasCriteria={!isNoHit && !!criteria}
          criteriaYears={bankruptcyYears}
          criteriaValue={criteria?.bankruptcy_records}
          additionalCriteriaValue={bankruptcyCriteriaValue}
          criteriaSuccess={
            (bankruptcy || 0) <= (criteria?.bankruptcy_records || 0)
          }
          criteriaImportance={screeningCriteria?.bankruptcy_records?.importance}
        />
        <Stat
          title="Debt balance"
          value={isNoHit ? "-" : "$" + String(debtBalance ?? "--")}
          width="240px"
        />
      </div>
    </Section>
  );
};
